/**
 * CR204: Layout Renderer
 * CR320: Added sort state management for layout-driven sorting
 * 
 * The AUTHORITATIVE renderer for public page content based on published layout.
 * This component is the SINGLE source of truth for page composition below AdminChrome.
 * 
 * Key behaviors:
 * - Iterates published layout blocks in order
 * - Only renders blocks where enabled === true
 * - Uses centralized BLOCK_REGISTRY for component mapping
 * - Provides context to all blocks (deals, filters, settings)
 * - Reports rendered blocks for debugging
 */

import { useMemo, useCallback } from "react";
import type { LayoutJson, LayoutBlockId, SortOptionId, CanvasArtboardId } from "@/lib/layout-studio/types";
import type { DealCardUXConfig } from "@/lib/layout-studio/dealCardUXTypes";
import type { Deal, DealCategory } from "@/lib/types/deals";
import { BLOCK_REGISTRY, isBlockRegistered, type BlockRenderContext, type BlockRenderResult } from "./blocks";

interface LayoutRendererProps {
  // Layout configuration
  layout: LayoutJson;
  dealCardUX: DealCardUXConfig;
  layoutLoading?: boolean;
  
  // Data
  deals: Deal[];
  dealsLoading?: boolean;
  
  // Search/Filter state (controlled by parent)
  searchQuery: string;
  onSearchChange: (value: string) => void;
  selectedCategory: string;
  onCategoryChange: (value: string) => void;
  selectedDealTypes: DealCategory[] | "All";
  onDealTypesChange: (types: DealCategory[] | "All") => void;
  
  // CR320: Sort state (controlled by parent, defaults from layout)
  selectedSort: SortOptionId;
  onSortChange: (sort: SortOptionId) => void;
  
  // Feature flags
  isAdmin?: boolean;
  
  // Debugging callback - reports which blocks were rendered
  onBlocksRendered?: (results: BlockRenderResult[]) => void;
}

export function LayoutRenderer({
  layout,
  dealCardUX,
  layoutLoading = false,
  deals,
  dealsLoading = false,
  searchQuery,
  onSearchChange,
  selectedCategory,
  onCategoryChange,
  selectedDealTypes,
  onDealTypesChange,
  selectedSort,
  onSortChange,
  isAdmin = false,
  onBlocksRendered,
}: LayoutRendererProps) {
  
  // Build block render context
  const context: BlockRenderContext = useMemo(() => ({
    deals,
    loading: layoutLoading || dealsLoading,
    searchQuery,
    onSearchChange,
    selectedCategory,
    onCategoryChange,
    selectedDealTypes,
    onDealTypesChange,
    selectedSort,
    onSortChange,
    layout,
    dealCardUX,
    isAdmin,
  }), [
    deals, layoutLoading, dealsLoading,
    searchQuery, onSearchChange,
    selectedCategory, onCategoryChange,
    selectedDealTypes, onDealTypesChange,
    selectedSort, onSortChange,
    layout, dealCardUX, isAdmin,
  ]);
  
  // Get enabled blocks sorted by order
  const enabledBlocks = useMemo(() => {
    return layout.blocks
      .filter((b) => b.enabled)
      .sort((a, b) => a.order - b.order);
  }, [layout.blocks]);

  const isCanvasMode = layout.editorMode === "canvas";
  const canvasArtboard: CanvasArtboardId = layout.canvas?.activeArtboard || "desktop";
  const canvasConfig = layout.canvas?.artboards?.[canvasArtboard];
  const canvasNodes = useMemo(() => {
    if (!isCanvasMode || !canvasConfig?.nodes) return [];
    return [...canvasConfig.nodes]
      .filter((n) => n.visible)
      .sort((a, b) => a.z - b.z);
  }, [isCanvasMode, canvasConfig?.nodes]);

  const blockById = useMemo(
    () => Object.fromEntries(layout.blocks.map((b) => [b.id, b])),
    [layout.blocks]
  );
  
  // Track which blocks were actually rendered
  const renderedBlockIds = useMemo(() => {
    const ids = new Set<LayoutBlockId>();
    enabledBlocks.forEach((block) => {
      if (isBlockRegistered(block.id)) {
        ids.add(block.id);
      }
    });
    return ids;
  }, [enabledBlocks]);
  
  // Report block render results for debugging
  useMemo(() => {
    if (onBlocksRendered) {
      const results: BlockRenderResult[] = layout.blocks
        .sort((a, b) => a.order - b.order)
        .map((block) => ({
          blockId: block.id,
          rendered: block.enabled && renderedBlockIds.has(block.id),
          reason: !block.enabled 
            ? "disabled" 
            : !isBlockRegistered(block.id) 
              ? "not in registry" 
              : undefined,
        }));
      onBlocksRendered(results);
    }
  }, [layout.blocks, renderedBlockIds, onBlocksRendered]);
  
  return (
    isCanvasMode ? (
      <div
        className="relative mx-auto border rounded-md bg-background"
        style={{
          width: canvasConfig?.width || 1200,
          minHeight: canvasConfig?.minHeight || 1400,
        }}
      >
        {(layout.canvas?.backgroundLayers || [])
          .filter((l) => l.visible)
          .sort((a, b) => a.z - b.z)
          .map((layer) => {
            if (layer.kind === "solid") {
              return (
                <div
                  key={layer.id}
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    backgroundColor: `hsl(${layer.solidColor || "0 0% 98%"})`,
                    opacity: layer.opacity ?? 1,
                  }}
                />
              );
            }
            if (layer.kind === "gradient") {
              const g = layer.gradient || { color1: "0 0% 98%", color2: "220 15% 95%", angle: 135 };
              return (
                <div
                  key={layer.id}
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background: `linear-gradient(${g.angle}deg, hsl(${g.color1}), hsl(${g.color2}))`,
                    opacity: layer.opacity ?? 1,
                  }}
                />
              );
            }
            if (layer.kind === "asset" && layer.asset?.assetId) {
              const fit = layer.asset.fit === "repeat" ? "auto" : layer.asset.fit;
              return (
                <div
                  key={layer.id}
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    backgroundImage: "radial-gradient(hsl(220 10% 92%) 1px, transparent 1px)",
                    backgroundRepeat: layer.asset.fit === "repeat" ? "repeat" : "no-repeat",
                    backgroundSize: layer.asset.fit === "repeat" ? "20px 20px" : fit,
                    backgroundPosition: "center",
                    opacity: layer.opacity ?? 1,
                  }}
                />
              );
            }
            return null;
          })}
        {canvasNodes.map((node) => {
          const block = blockById[node.blockId];
          if (!block?.enabled || !isBlockRegistered(node.blockId)) return null;
          const BlockComponent = BLOCK_REGISTRY[node.blockId];
          return (
            <div
              key={node.id}
              className="absolute overflow-auto"
              data-block-id={node.blockId}
              style={{
                left: node.rect.x,
                top: node.rect.y,
                width: node.rect.w,
                height: node.rect.h,
                zIndex: node.z,
              }}
            >
              <BlockComponent config={block.config || {}} context={context} />
            </div>
          );
        })}
      </div>
    ) : (
      <div className="space-y-8">
        {enabledBlocks.map((block) => {
          // Skip blocks not in registry
          if (!isBlockRegistered(block.id)) {
            console.warn(`[LayoutRenderer] Block "${block.id}" is not registered`);
            return null;
          }
          
          const BlockComponent = BLOCK_REGISTRY[block.id];
          const config = block.config || {};
          
          return (
            <div key={block.id} data-block-id={block.id} data-block-order={block.order}>
              <BlockComponent config={config} context={context} />
            </div>
          );
        })}
      </div>
    )
  );
}
